import { gql } from '@apollo/client';
import {
  CreatorFields,
  PartitionFieldsFragment,
  PartitionValueFieldsFragment,
  SchemaFieldsFragment,
  FunnelStepBriefInfoFields,
  SubjectFragment,
  UserAliasQueryMeasureFragment,
  EditorFields,
} from './fragments';
import { CohortRefFragment } from './cohorts';
import { MeasureFragment, MeasurePropertiesFragment } from './measures';
import { EventConditionFieldsFragment } from './events/eventConditions';
import { FilterFragment, PredicateFieldsFragment } from './filters';
import { FieldFieldsFragment } from './fields';
import { EntityCategoryFragment } from './entityCategories';
import { LlmChatMessageFieldsFragment } from './llm/fragments';

/* ********************* Basic Fragments ********************* */

export const AttributionFragment = gql`
  fragment AttributionFields on Attribution {
    key
    name
  }
`;
export const GroupbyFragment = gql`
  fragment GroupbyFields on Groupby {
    key
    name
  }
`;
export const RetentionViewFragment = gql`
  fragment RetentionViewFields on RetentionView {
    key
    name
  }
`;

export const DateKindFragment = gql`
  fragment DateKindFields on DateKind {
    key
    name
  }
`;

export const DateKindSupportFragment = gql`
  fragment DateKindSupportFields on DateKind {
    key
    name
    supportedTimeUnits
  }
`;

export const FunnelModeFieldsFragment = gql`
  fragment FunnelModeFields on FunnelMode {
    name
    key
  }
`;

export const PathMergeEventFieldsFragment = gql`
  fragment PathMergeEventFields on PathMergeEvent {
    name
    isNameAutoGenerated
    eventConditions {
      ...EventConditionFields
    }
  }
  ${EventConditionFieldsFragment}
`;

export const PathExpandEventFieldsFragment = gql`
  fragment PathExpandEventFields on PathExpandEvent {
    field {
      ...FieldFields
    }
    eventCondition {
      ...EventConditionFields
    }
  }
  ${FieldFieldsFragment}
  ${EventConditionFieldsFragment}
`;

export const PathFoldEventFieldsFragment = gql`
  fragment PathFoldEventFields on PathFoldEvent {
    eventCondition {
      ...EventConditionFields
    }
  }
  ${EventConditionFieldsFragment}
`;

export const DateBucketFragment = gql`
  fragment DateBucketFields on DateBucket {
    name
    kind
    minDate
    maxDate
  }
`;

export const EnumBucketFragment = gql`
  fragment EnumBucketFields on EnumBucket {
    name
    kind
    values {
      key
      name
    }
    isCustomName
  }
`;

export const RangeBucketFragment = gql`
  fragment RangeBucketFields on RangeBucket {
    name
    kind
    min
    max
    isCustomName
  }
`;

export const BucketFragment = gql`
  fragment BucketFields on Bucket {
    ... on RangeBucket {
      ...RangeBucketFields
    }
    ... on EnumBucket {
      ...EnumBucketFields
    }
    ... on DateBucket {
      ...DateBucketFields
    }
  }
  ${DateBucketFragment}
  ${EnumBucketFragment}
  ${RangeBucketFragment}
`;

export const NamedBucketDefFragment = gql`
  fragment NamedBucketDefFields on NamedBucketDef {
    id
    name
    type
    kind
    buckets {
      ...BucketFields
    }
    version
    isLatestVersion
    isDisabled
    comment
    description
    createdAt
    creator {
      ...CreatorFields
    }
    editor {
      ...EditorFields
    }
    editedAt
    enumBucketField {
      ...FieldFields
    }
    enumBucketSplitOthers
    categories {
      ...EntityCategoryFields
    }
    isFavorite
    isManaged
  }
  ${FieldFieldsFragment}
  ${BucketFragment}
  ${CreatorFields}
  ${EntityCategoryFragment}
  ${EditorFields}
`;

export const BasicBucketDefFragment = gql`
  fragment BasicBucketDefFields on BasicBucketDef {
    id
    type
    kind
    enumBucketSplitOthers
    buckets {
      ...BucketFields
    }
  }
  ${BucketFragment}
`;

export const BucketDefFragment = gql`
  fragment BucketDefFields on BucketDef {
    ... on BasicBucketDef {
      ...BasicBucketDefFields
    }
    ... on NamedBucketDef {
      ...NamedBucketDefFields
    }
  }
  ${NamedBucketDefFragment}
  ${BasicBucketDefFragment}
`;

export const DurationFragment = gql`
  fragment DurationFields on Duration {
    timeUnit
    value
  }
`;
// negative step doesn't have conversion Window
export const NegativeStepFragment = gql`
  fragment NegativeFunnelStepFields on FunnelStep {
    ...FunnelStepBriefInfoFields
    stepConditions {
      ...EventConditionFields
    }
  }
  ${EventConditionFieldsFragment}
  ${FunnelStepBriefInfoFields}
`;

export const FunnelStepFieldsFragment = gql`
  fragment FunnelStepFields on FunnelStep {
    ...FunnelStepBriefInfoFields
    stepConditions {
      ...EventConditionFields
    }
    conversionWindow {
      ...DurationFields
    }
  }
  ${FunnelStepBriefInfoFields}
  ${EventConditionFieldsFragment}
  ${DurationFragment}
`;

export const FunnelStepOrderFieldsFragment = gql`
  fragment FunnelStepOrderFields on FunnelOrder {
    name
    key
  }
`;

export const RetentionCalculationFragment = gql`
  fragment RetentionCalculationFields on RetentionCalculation {
    key
    name
  }
`;

export const RetentionCalculationWindowFragment = gql`
  fragment RetentionCalculationWindowFields on RetentionCalculationWindow {
    key
    name
  }
`;

/* *************** End of basic fragments ***************** */

export const BreakdownFieldsFragment = gql`
  fragment BreakdownFields on Breakdown {
    groupbys {
      field {
        ...FieldFields
      }
      bucketDef {
        ...BucketDefFields
      }
      predicate {
        ...PredicateFields
      }
    }
    cohortRefs {
      ...CohortRefFields
    }
    overall
    order
  }
  ${BucketDefFragment}
  ${FieldFieldsFragment}
  ${CohortRefFragment}
  ${PredicateFieldsFragment}
`;

export const ExpressionModeFieldsFragment = gql`
  fragment ExpressionModeFields on ExpressionMode {
    key
    name
  }
`;

export const ExpressionQueryFieldsFragment = gql`
  fragment ExpressionQueryFields on ExpressionQuery {
    filter {
      ...FilterFields
    }
    breakdown {
      ...BreakdownFields
    }
    dateKind {
      ...DateKindFields
    }
    schema {
      ...SchemaFields
    }
    name
    isNameAutoGenerated
    timeWindow
    includeMissingDates
    subjectBucketDef {
      ...BucketDefFields
    }
    subject {
      ...SubjectFields
    }
    triggerSubject {
      key
      name
    }
    triggerCondition {
      ...EventConditionFields
    }
    cohortRefs {
      ...CohortRefFields
    }
    compoundMeasureUserAlias {
      ...UserAliasQueryMeasureFields
    }
    samplePercentage
    measure {
      ...MeasureFields
    }
    measureProperties {
      ...MeasurePropertiesFields
    }
    expressionSettings: settings
    expressionMode: mode {
      ...ExpressionModeFields
    }
    llmChatMessages {
      ...LlmChatMessageFields
    }
  }
  ${CohortRefFragment}
  ${SubjectFragment}
  ${BucketDefFragment}
  ${EventConditionFieldsFragment}
  ${SchemaFieldsFragment}
  ${DateKindFragment}
  ${BreakdownFieldsFragment}
  ${FilterFragment}
  ${UserAliasQueryMeasureFragment}
  ${MeasureFragment}
  ${MeasurePropertiesFragment}
  ${ExpressionModeFieldsFragment}
  ${LlmChatMessageFieldsFragment}
`;

export const FunnelQueryFieldsFragment = gql`
  fragment FunnelQueryFields on FunnelQuery {
    steps {
      ...FunnelStepFields
    }
    stepOrders {
      ...FunnelStepOrderFields
    }
    negativeSteps {
      ...NegativeFunnelStepFields
    }
    dateKind {
      ...DateKindFields
    }
    filter {
      ...FilterFields
    }
    breakdown {
      ...BreakdownFields
    }
    schema {
      ...SchemaFields
    }
    partitions {
      ...PartitionValueFields
    }
    subject {
      ...SubjectFields
    }
    order {
      key
      name
    }
    timeWindow
    cohortRefs {
      ...CohortRefFields
    }
    groupby {
      ...GroupbyFields
    }
    mode {
      ...FunnelModeFields
    }
    samplePercentage
    funnelSettings: settings
    timeUnit
    modeSlices {
      ...FunnelModeSliceFields
    }
    llmChatMessages {
      ...LlmChatMessageFields
    }
  }
  ${GroupbyFragment}
  ${CohortRefFragment}
  ${SubjectFragment}
  ${PartitionFieldsFragment}
  ${PartitionValueFieldsFragment}
  ${SchemaFieldsFragment}
  ${DateKindFragment}
  ${BreakdownFieldsFragment}
  ${FilterFragment}
  ${FunnelStepFieldsFragment}
  ${NegativeStepFragment}
  ${FunnelStepOrderFieldsFragment}
  ${FunnelModeFieldsFragment}
  ${LlmChatMessageFieldsFragment}
`;

export const PathQueryFieldsFragment = gql`
  fragment PathQueryFields on PathQuery {
    startConditions {
      ...EventConditionFields
    }
    endConditions {
      ...EventConditionFields
    }
    filter {
      ...FilterFields
    }
    direction {
      key
      name
    }
    breakdown {
      ...BreakdownFields
    }

    schema {
      ...SchemaFields
    }
    partitions {
      ...PartitionValueFields
    }
    subject {
      ...SubjectFields
    }
    cohortRefs {
      ...CohortRefFields
    }
    groupby {
      ...GroupbyFields
    }
    mergeEvents {
      ...PathMergeEventFields
    }
    expandEvents {
      ...PathExpandEventFields
    }
    foldEvents {
      ...PathFoldEventFields
    }
    pathMode: mode {
      name
      key
    }
    samplePercentage
    pathSettings: settings
    llmChatMessages {
      ...LlmChatMessageFields
    }
  }
  ${GroupbyFragment}
  ${CohortRefFragment}
  ${PartitionFieldsFragment}
  ${PartitionValueFieldsFragment}
  ${SubjectFragment}
  ${SchemaFieldsFragment}
  ${BreakdownFieldsFragment}
  ${FieldFieldsFragment}
  ${FilterFragment}
  ${EventConditionFieldsFragment}
  ${PathMergeEventFieldsFragment}
  ${PathExpandEventFieldsFragment}
  ${PathFoldEventFieldsFragment}
  ${LlmChatMessageFieldsFragment}
`;

export const RetentionQueryFieldsFragment = gql`
  fragment RetentionQueryFields on RetentionQuery {
    startConditions {
      ...EventConditionFields
    }
    returnConditions {
      ...EventConditionFields
    }
    filter {
      ...FilterFields
    }
    breakdown {
      ...BreakdownFields
    }
    schema {
      ...SchemaFields
    }
    calculation {
      ...RetentionCalculationFields
    }
    calculationWindow {
      ...RetentionCalculationWindowFields
    }
    cohortUnit
    retentionUnit
    timeWindow
    subject {
      ...SubjectFields
    }
    cohortRefs {
      ...CohortRefFields
    }
    customTimeBrackets
    attribution {
      ...AttributionFields
    }
    groupby {
      ...GroupbyFields
    }
    view {
      ...RetentionViewFields
    }
    samplePercentage
    retentionSettings: settings
    llmChatMessages {
      ...LlmChatMessageFields
    }
  }
  ${AttributionFragment}
  ${GroupbyFragment}
  ${CohortRefFragment}
  ${SubjectFragment}
  ${SchemaFieldsFragment}
  ${BreakdownFieldsFragment}
  ${FieldFieldsFragment}
  ${FilterFragment}
  ${EventConditionFieldsFragment}
  ${RetentionCalculationFragment}
  ${RetentionCalculationWindowFragment}
  ${RetentionViewFragment}
  ${LlmChatMessageFieldsFragment}
`;

export const PredictionQueryFieldsFragment = gql`
  fragment PredictionQueryFields on PredictionQuery {
    sourceCohortRef {
      ...CohortRefFields
    }
    targetCohortRef {
      ...CohortRefFields
    }
    window
    eventCondition {
      ...EventConditionFields
    }
    schema {
      ...SchemaFields
    }
    samplePercentage
  }
  ${CohortRefFragment}
  ${EventConditionFieldsFragment}
  ${SchemaFieldsFragment}
`;

export const DataTableQueryFieldsFragment = gql`
  fragment DataTableQueryFields on DataTableQuery {
    filter {
      ...FilterFields
    }
    breakdown {
      ...BreakdownFields
    }
    dateKind {
      ...DateKindFields
    }
    schema {
      ...SchemaFields
    }
    timeWindow
    cohortRefs {
      ...CohortRefFields
    }
    samplePercentage
    measures {
      ...MeasureFields
    }
    measuresProperties {
      ...MeasurePropertiesFields
    }
    dataTableSettings: settings
    llmChatMessages {
      ...LlmChatMessageFields
    }
  }
  ${CohortRefFragment}
  ${SchemaFieldsFragment}
  ${DateKindFragment}
  ${BreakdownFieldsFragment}
  ${FilterFragment}
  ${MeasureFragment}
  ${MeasurePropertiesFragment}
  ${LlmChatMessageFieldsFragment}
`;
